"use client";

import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  buildVariantLookup,
  computeAvailability,
  firstUnmetOption,
  resolveVariant,
  selectionForVariant,
  selectReachableOption,
  withVariantParam,
  type VariantSelection,
} from "~/components/product/variant/availability";
import { VariantContext } from "~/components/product/variant/context";
import type { PdpOption, PdpVariant } from "~/server/api/schemas/pdp";

interface VariantProviderProps {
  options: PdpOption[];
  variants: PdpVariant[];
  initialVariantId: string | null;
  children: ReactNode;
}

export function VariantProvider({
  options,
  variants,
  initialVariantId,
  children,
}: VariantProviderProps) {
  const [selected, setSelected] = useState<VariantSelection>(() =>
    selectionForVariant(
      options,
      variants.find((variant) => variant.id === initialVariantId),
    ),
  );

  const [selectionNotice, setSelectionNotice] = useState<string | null>(null);
  const lookup = useMemo(() => buildVariantLookup(variants), [variants]);
  const availability = useMemo(
    () => computeAvailability(options, variants, selected),
    [options, variants, selected],
  );
  const resolvedVariant = useMemo(
    () => resolveVariant(options, variants, selected, lookup),
    [lookup, options, selected, variants],
  );
  const unmetOption = firstUnmetOption(options, selected);
  const isComplete = unmetOption === null && resolvedVariant !== undefined;
  const validationMessage = unmetOption
    ? `Select ${unmetOption.name.toLocaleLowerCase()}`
    : resolvedVariant
      ? null
      : "This combination is unavailable";

  const selectOption = useCallback(
    (optionId: string, optionValueId: string) => {
      const option = options.find((candidate) => candidate.id === optionId);
      const belongsToOption = option?.values.some(
        (value) => value.id === optionValueId,
      );
      if (
        !belongsToOption ||
        !(availability[optionValueId]?.selectable ?? availability[optionValueId]?.available) ||
        selected[optionId] === optionValueId
      ) {
        return;
      }

      const next = selectReachableOption(options, variants, selected, optionId, optionValueId);
      if (!next) return;
      const cleared = options.filter(candidate => selected[candidate.id] && !next[candidate.id]);
      setSelectionNotice(cleared.length ? `Choose ${cleared.map(candidate => candidate.name).join(", ")} for this option.` : null);
      setSelected(next);
    },
    [availability, options, selected, variants],
  );

  const clearOption = useCallback(
    (optionId: string) => {
      if (selected[optionId] === undefined) return;
      const next = { ...selected };
      delete next[optionId];
      setSelected(next);
    },
    [selected],
  );

  // Sync the selection to `?variant=` for deep-linking/sharing. Uses a shallow
  // history update (not router.replace) so it never triggers an RSC navigation
  // — the page's Suspense boundary is keyed on the slug alone, so variant
  // changes stay entirely client-side (no remount, no skeleton flash, no
  // duplicate product.viewed). Skipped on the first render: the server already
  // emitted the canonical URL (a `?variant=` deep-link or none for the
  // default), so writing on mount would only add a redundant param.
  const hasSyncedOnce = useRef(false);
  useEffect(() => {
    if (!hasSyncedOnce.current) {
      hasSyncedOnce.current = true;
      return;
    }
    const search = new URLSearchParams(window.location.search);
    const nextQuery = withVariantParam(search, resolvedVariant?.id ?? null);
    if (nextQuery === search.toString()) return;
    const url = nextQuery
      ? `${window.location.pathname}?${nextQuery}`
      : window.location.pathname;
    window.history.replaceState(window.history.state, "", url);
  }, [resolvedVariant?.id]);

  const value = useMemo(
    () => ({
      selected,
      resolvedVariant,
      availability,
      isComplete,
      firstUnmetOption: unmetOption,
      validationMessage,
      selectOption,
      clearOption,
    }),
    [
      availability,
      clearOption,
      isComplete,
      resolvedVariant,
      selectOption,
      selected,
      unmetOption,
      validationMessage,
    ],
  );

  return (
    <VariantContext.Provider value={value}><span role="status" className="sr-only">{selectionNotice}</span>{children}</VariantContext.Provider>
  );
}
